/**
 * Color Fill Legend Tool — Annotate > Color Fill panel.
 *
 * Places the legend box that documents the color-fill scheme of the active
 * view. Two modes mirror BIM's panel:
 *   rooms — architectural room color fills keyed on a room parameter
 *           (Room Name / Department / Area)
 *   systems — mechanical duct/pipe color fills keyed on the system type
 *
 * Clicking in the view drops the legend; the tool scans the model, assigns a
 * stable palette color to every distinct key value, and stores the result on
 * both the legend element and the matched elements ('Color Fill Color'
 * parameter) so the renderers paint the fills.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import i18n from '../i18n';

export type ColorFillMode = 'rooms' | 'systems';

export interface LegendEntry {
    label: string;
    color: string;
    count: number;
}

export interface ColorFillLegendGeometryData {
    position: [number, number, number];
    mode: ColorFillMode;
    parameter: string;
    entries: LegendEntry[];
}

export interface ColorFillLegendElement extends Element {
    category: 'Detail Items';
    geometryData: ColorFillLegendGeometryData;
}

/**
 * Deterministic palette (BIM's room palette, extended): same key value
 * always receives the same color across placements.
 */
const PALETTE = [
    '#e6555f', '#5fb86a', '#5a8fe6', '#e6c15a', '#9a6ae6',
    '#5ac8c8', '#e68a5a', '#6ae69a', '#8a9ae6', '#e65aa8',
    '#c8b45a', '#5a9ae6', '#b05ae6', '#5ae67a', '#e67a5a',
];

function colorForKey(key: string, index: number): string {
    // Stable hash so a value keeps its color even if insertion order shifts.
    let h = index;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return PALETTE[h % PALETTE.length];
}

export class ColorFillLegendTool extends BaseTool {
    id = 'color_fill_legend';
    name: string;
    cursor = 'crosshair';

    private mode: ColorFillMode;

    constructor(mode: ColorFillMode = 'rooms') {
        super();
        this.mode = mode;
        this.id = mode === 'rooms' ? 'color_fill_legend' : 'color_fill_legend_systems';
        this.name = mode === 'rooms' ? 'Color Fill Legend' : 'Duct / Pipe Color Fill Legend';
    }

    protected onActivate(): void {
        this.notify(
            this.mode === 'rooms'
                ? 'Color Fill Legend: click to place. Room fills are colored by the selected parameter.'
                : 'Duct / Pipe Color Fill Legend: click to place. Systems are colored by system type.',
        );
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point, 0.1);
        this.createLegend(snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.context.appStore.setToolPreview({
                type: 'color_fill_legend',
                mode: this.mode,
                position: point,
            });
        }
    }

    onPointerUp(): void { }

    /** Distinct key values across the fillable elements + counts. */
    private collectEntries(): { entries: LegendEntry[]; colors: Map<string, string> } {
        const colors = new Map<string, string>();
        const counts = new Map<string, number>();
        if (!this.context) return { entries: [], colors };

        const project = this.context.bimStore.project;
        const parameter = this.mode === 'rooms'
            ? (this.context.bimStore.toolProperties.colorFillParameter ?? 'Name')
            : 'System Type';

        // First pass: read only. Mutating the store while iterating the
        // element map could invalidate the traversal.
        const targets: { id: string; key: string }[] = [];
        project.elements.forEach((el, id) => {
            let key: string | undefined;
            if (this.mode === 'rooms' && el.category === 'Rooms') {
                key = String(el.parameters?.[parameter] ?? el.parameters?.['Name'] ?? 'Room');
            } else if (this.mode === 'systems'
                && (el.category === 'Ducts' || el.category === 'Pipes')) {
                key = String(el.parameters?.[parameter]
                    ?? (el as any).geometryData?.systemType
                    ?? 'Unassigned');
            }
            if (!key) return;
            targets.push({ id, key });
            counts.set(key, (counts.get(key) ?? 0) + 1);
            if (!colors.has(key)) colors.set(key, colorForKey(key, colors.size + 1));
        });

        // Second pass: write the resolved color back so renderers paint fills.
        targets.forEach(({ id, key }) => {
            const el = this.context?.bimStore.project.elements.get(id);
            if (!el) return;
            this.context?.bimStore.updateElement(id, {
                parameters: { ...el.parameters, 'Color Fill Color': colors.get(key) },
            });
        });

        const entries: LegendEntry[] = Array.from(counts.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([label, count]) => ({ label, color: colors.get(label) ?? '#999999', count }));
        return { entries, colors };
    }

    private createLegend(point: THREE.Vector3) {
        if (!this.context) return;

        const { entries } = this.collectEntries();
        if (entries.length === 0) {
            this.notify(
                this.mode === 'rooms'
                    ? 'Color Fill Legend: no rooms found — place rooms first.'
                    : 'Color Fill Legend: no ducts or pipes found — draw systems first.',
                'warning',
            );
            return;
        }

        const parameter = this.mode === 'rooms'
            ? (this.context.bimStore.toolProperties.colorFillParameter ?? 'Name')
            : 'System Type';

        const geometryData: ColorFillLegendGeometryData = {
            position: [point.x, point.y, point.z],
            mode: this.mode,
            parameter,
            entries,
        };

        const legend: ColorFillLegendElement = {
            id: uuidv4(),
            category: 'Detail Items',
            type: this.name,
            parameters: {
                'Legend Type': this.name,
                'Color Fill Criteria': parameter,
                'Entries': entries.length,
            },
            constraints: [],
            geometryData,
        };

        this.context.bimStore.addElement(legend);
        this.notify(i18n.t('tools.notify.legendEntries', { tool: this.name, count: entries.length }), 'success');
    }
}
